import { actionCreator } from 'app-webtech-core/actions/actionCreator';
import { Joi } from 'app-webtech-core/schemas/index';
import { actions, selectors } from 'app-webtech-core';
import {
  CM_BEGIN,
  CM_COMPLETE,
  CM_END,
  CM_ERROR,
  CM_FIRST_QUARTILE,
  CM_IMPRESSION,
  CM_MID_POINT,
  CM_POD_BEGIN,
  CM_POD_COMPLETE,
  CM_POD_END,
  CM_POD_START,
  CM_START,
  CM_THIRD_QUARTILE,
} from './types';
import { AD_EVENT_NAMES, AD_POD_EVENT_NAMES, VAST_EVENT_NAMES } from '../constants';

const schemaAd = Joi.object().keys({
  ad: Joi.object().required(),
  adBreakDuration: Joi.number().required(),
  adIndex: Joi.number().default(-1),
});

const schemaPod = Joi.object().keys({
  adBreakDuration: Joi.number().required(),
  adIndex: Joi.number().default(-1),
});

const schemaError = Joi.object().keys({
  ad: Joi.object().required(),
  adBreakDuration: Joi.number().required(),
  adIndex: Joi.number().default(-1),
  type: Joi.string().required(),
  message: Joi.string().required(),
});

export const cmBegin = actionCreator(CM_BEGIN, schemaAd);
export const cmEnd = actionCreator(CM_END, schemaAd);
export const cmStart = actionCreator(CM_START, schemaAd);
export const cmFirstQuartile = actionCreator(CM_FIRST_QUARTILE, schemaAd);
export const cmMidPoint = actionCreator(CM_MID_POINT, schemaAd);
export const cmThirdQuartile = actionCreator(CM_THIRD_QUARTILE, schemaAd);
export const cmComplete = actionCreator(CM_COMPLETE, schemaAd);

export const cmImpression = actionCreator(CM_IMPRESSION, schemaAd);

export const cmPodStart = actionCreator(CM_POD_START, schemaPod);
export const cmPodComplete = actionCreator(CM_POD_COMPLETE, schemaPod);
export const cmPodBegin = actionCreator(CM_POD_BEGIN, schemaPod);
export const cmPodEnd = actionCreator(CM_POD_END, schemaPod);

export const cmError = actionCreator(CM_ERROR, schemaError);

/*
 Temporary hack (see NNVTN-1025) until REV-659 is implemented
 */
const PROTOCOL_REGEX = /^\s*http:\/\//i;

// we consider an event fired on time if it was fired up to ACCEPTABLE_MISSING_MS
// milliseconds after the scheduled time. This is necessary because the player
// 'time' event is not fired each ms but a couple of times each second
const ACCEPTABLE_MISSING_MS = 500;

const doFetch = (ad, adBreakDuration, adIndex, urlP, dispatch) => {
  const url = urlP.replace(PROTOCOL_REGEX, '//');
  fetch(url, {
    mode: 'no-cors',
  })
    .then((response) => {
      if (!response.ok) {
        dispatch(
          cmError({
            ad,
            adBreakDuration,
            adIndex,
            type: response.type,
            message: `${response.statusText}:${url}`,
          }),
        );
      }
    })
    .catch((error) => {
      dispatch(
        cmError({
          ad,
          adBreakDuration,
          adIndex,
          type: error.type,
          message: `${error.message}:${url}`,
        }),
      );
    });
};

// Fire also VAST events
const fireVastEvents = (ad, adBreakDuration, adIndex, vastEventName, dispatch) => {
  const { trackingEvents = [] } = ad;
  trackingEvents.forEach((e) => {
    if (e.event === vastEventName) {
      doFetch(ad, adBreakDuration, adIndex, e.url, dispatch);
    }
  });
};

/**
 *
 * @param name
 * @param ad
 * @param adBreakDuration
 * @param missedBy: how many ms after the scheduled time, was the event actually fired.
 * If we land on the Ad after it started (or even after finished), the missedBy will be
 * big. If we land before/on time, it will be a few ms (see ACCEPTABLE_MISSING_MS const).
 * For cmEnd and cmPodEnd it may be negative if the event was fired before scheduled time.
 * That happens if the user changed channel in the middle of an Ad: all the cm*100 already
 * scheduled events are fired at once.
 * @returns {function(*, *)}
 */

let adIndex = 0;
export const cmEventAction = ({ name, ad, adBreakDuration, missedBy }) => async (
  dispatch,
  getState,
) => {
  // eslint-disable-line no-unused-vars
  switch (name) {
    case AD_EVENT_NAMES.AD_0_PERCENT:
      if (missedBy < ad.duration && missedBy >= 0) {
        adIndex += 1;
        dispatch(cmBegin({ ad, adBreakDuration, adIndex }));
      }
      if (missedBy < ACCEPTABLE_MISSING_MS && missedBy >= 0 && missedBy < ad.duration) {
        dispatch(cmImpression({ ad, adBreakDuration, adIndex }));
        dispatch(cmStart({ ad, adBreakDuration, adIndex }));
        if (ad && ad.impressions && ad.impressions.length > 0) {
          ad.impressions.forEach((url) => doFetch(ad, adBreakDuration, adIndex, url, dispatch)); // VAST
        }
        fireVastEvents(ad, adBreakDuration, adIndex, VAST_EVENT_NAMES.START, dispatch);
      }
      break;
    case AD_EVENT_NAMES.AD_25_PERCENT:
      if (missedBy < ACCEPTABLE_MISSING_MS) {
        dispatch(cmFirstQuartile({ ad, adBreakDuration, adIndex }));
        fireVastEvents(ad, adBreakDuration, adIndex, VAST_EVENT_NAMES.FIRST_QUARTILE, dispatch);
      }
      break;
    case AD_EVENT_NAMES.AD_50_PERCENT:
      if (missedBy < ACCEPTABLE_MISSING_MS) {
        dispatch(cmMidPoint({ ad, adBreakDuration, adIndex }));
        fireVastEvents(ad, adBreakDuration, VAST_EVENT_NAMES.MIDPOINT, dispatch);
      }
      break;
    case AD_EVENT_NAMES.AD_75_PERCENT:
      if (missedBy < ACCEPTABLE_MISSING_MS) {
        dispatch(cmThirdQuartile({ ad, adBreakDuration, adIndex }));
        fireVastEvents(ad, adBreakDuration, adIndex, VAST_EVENT_NAMES.THIRD_QUARTILE, dispatch);
      }
      break;
    case AD_EVENT_NAMES.AD_100_PERCENT:
      if (missedBy < ACCEPTABLE_MISSING_MS) {
        if (missedBy >= 0) {
          dispatch(cmComplete({ ad, adBreakDuration, adIndex }));
          fireVastEvents(ad, adBreakDuration, adIndex, VAST_EVENT_NAMES.COMPLETE, dispatch);
        }
        dispatch(cmEnd({ ad, adBreakDuration, adIndex }));
      }
      break;
    case AD_POD_EVENT_NAMES.AD_POD_0_PERCENT:
      dispatch(actions.player.clipEnds());
      dispatch(actions.podTrackerAds.setAdsPlaying({ isAdsPlaying: true }));
      dispatch(cmPodBegin({ adBreakDuration }));
      if (missedBy < ACCEPTABLE_MISSING_MS) {
        dispatch(cmPodStart({ adBreakDuration }));
      }
      break;
    case AD_POD_EVENT_NAMES.AD_POD_100_PERCENT:
      if (missedBy < ACCEPTABLE_MISSING_MS && missedBy >= 0) {
        dispatch(cmPodComplete({ adBreakDuration }));
        adIndex = 0;
      }
      dispatch(cmPodEnd({ adBreakDuration }));
      if (selectors.podTrackerAds.properties(getState()).isAdsPlaying) {
        dispatch(actions.player.clipStarts());
      }
      break;
    default:
      break;
  }
};
